import React, { useState } from "react";
import SEO from "@/components/SEO";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { BokehParticles } from "@/components/effects/BokehParticles";
import { LightStreaks } from "@/components/effects/LightStreaks";
import { Reveal } from "@/components/effects/Reveal";
import { Mail, Lock, User, ArrowRight, Phone } from "lucide-react";
import { useCustomerAuth } from "@/customer/AuthContext";
import { fmtErr } from "@/customer/api";

const inputBase = "w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-all font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]";
const Tab = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`flex-1 py-3 text-[11px] tracking-luxe uppercase transition-all ${active ? "text-white" : "text-white/45 hover:text-white/80"}`}>
    <span className="relative inline-flex flex-col items-center gap-1.5">{children}
      <span className={`h-px w-10 transition-all ${active ? "bg-gradient-to-r from-[#ff2e93] to-[#18d3ff]" : "bg-transparent"}`} />
    </span>
  </button>
);

export default function Auth() {
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", surname: "", email: "", password: "", confirm: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/customer";

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        if (!form.email || !form.password) { toast.error("Email and password are required."); return; }
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        if (!form.name || !form.surname || !form.email || !form.password) { toast.error("Please fill in all required fields."); return; }
        if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (form.password !== form.confirm) { toast.error("Passwords do not match."); return; }
        await register({ name: form.name, surname: form.surname, email: form.email, password: form.password, phone: form.phone });
        toast.success("Account created.");
      }
      navigate(from, { replace: true });
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-auth" className="relative min-h-[100svh] overflow-hidden flex items-center justify-center pt-32 pb-24">
      <SEO title="Sign in or Register — WelkomBiz Photography" description="Sign in to your WelkomBiz Photography account to browse purchased photos and downloads." canonicalPath="/auth" />
      <div className="absolute inset-0 -z-10" style={{
        background: "radial-gradient(70% 60% at 50% 0%, rgba(255,46,147,0.12), transparent 60%), radial-gradient(70% 60% at 50% 100%, rgba(24,211,255,0.10), transparent 60%), linear-gradient(180deg, #000, #050507 50%, #000)",
      }} />
      <div className="aperture-ring absolute inset-0 m-auto w-[60vmin] h-[60vmin] opacity-20" />
      <LightStreaks count={4} />
      <BokehParticles count={12} />

      <div className="relative w-full max-w-[480px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-8">
            <Logo size={72} linkTo={null} />
            <p className="mt-6 text-[11px] tracking-luxe uppercase text-white/45">— Customer Portal</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl"><span className="text-chrome">Welcome to your</span> <span className="italic text-magcyan">private gallery</span></h1>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <form onSubmit={submit} data-testid="auth-form" className="glass-strong chrome-border rounded-md p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />
            <div className="relative flex border-b border-white/8 mb-6">
              <Tab active={tab === "login"} onClick={() => setTab("login")} testId="auth-tab-login">Login</Tab>
              <Tab active={tab === "register"} onClick={() => setTab("register")} testId="auth-tab-register">Register</Tab>
            </div>
            <div className="relative space-y-4">
              {tab === "register" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><User className="w-3 h-3" /> Name</span>
                      <input data-testid="auth-input-name" value={form.name} onChange={upd("name")} placeholder="First name" className={inputBase} />
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><User className="w-3 h-3" /> Surname</span>
                      <input data-testid="auth-input-surname" value={form.surname} onChange={upd("surname")} placeholder="Surname" className={inputBase} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><Phone className="w-3 h-3" /> Phone (optional)</span>
                    <input data-testid="auth-input-phone" value={form.phone} onChange={upd("phone")} placeholder="+27 ..." className={inputBase} />
                  </label>
                </>
              )}
              <label className="block">
                <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><Mail className="w-3 h-3" /> Email</span>
                <input data-testid="auth-input-email" type="email" value={form.email} onChange={upd("email")} placeholder="you@example.com" className={inputBase} />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><Lock className="w-3 h-3" /> Password</span>
                <input data-testid="auth-input-password" type="password" value={form.password} onChange={upd("password")} placeholder="••••••••" className={inputBase} />
              </label>
              {tab === "register" && (
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2"><Lock className="w-3 h-3" /> Confirm Password</span>
                  <input data-testid="auth-input-confirm" type="password" value={form.confirm} onChange={upd("confirm")} placeholder="••••••••" className={inputBase} />
                </label>
              )}
              <button type="submit" data-testid="auth-submit" disabled={busy} className="btn-primary w-full mt-2 disabled:opacity-60">
                {busy ? "Please wait…" : (tab === "login" ? "Sign In" : "Create Account")}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              {tab === "login" && (
                <div className="text-center">
                  <Link to="/forgot-password" data-testid="auth-forgot-password" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
          </form>
        </Reveal>
        <p className="text-center mt-8 text-[11px] tracking-luxe uppercase text-white/40">
          <Link to="/" className="hover:text-white">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
